/**
 * AuditLogPage
 *
 * Admin page for viewing audit logs of all admin actions.
 */

import { useState } from 'react';
import { AdminLayout } from '../layout';
import { useAuditLog, getActionLabel, getActionColor } from './hooks';
import type { AuditFilter, AuditActionType } from './hooks';

const formatTimestamp = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatTimestamp(date);
};

const ACTION_TYPES: { value: AuditActionType | ''; label: string }[] = [
  { value: '', label: 'All Actions' },
  { value: 'role_change', label: 'Role Changes' },
  { value: 'user_suspended', label: 'User Suspensions' },
  { value: 'user_unsuspended', label: 'User Unsuspensions' },
  { value: 'listing_paused', label: 'Listing Paused' },
  { value: 'listing_approved', label: 'Listing Approved' },
  { value: 'listing_featured', label: 'Listing Featured' },
  { value: 'listing_deleted', label: 'Listing Deleted' },
  { value: 'payout_completed', label: 'Payout Completed' },
  { value: 'payout_rejected', label: 'Payout Rejected' },
  { value: 'platform_config_updated', label: 'Config Updates' },
  { value: 'booking_cancelled', label: 'Booking Cancelled' },
  { value: 'refund_processed', label: 'Refund Processed' },
];

export const AuditLogPage = () => {
  const [filter, setFilter] = useState<AuditFilter>({});
  const { logs, totalCount, isLoading } = useAuditLog(filter);

  const renderDetails = (details: Record<string, any>) => {
    const entries = Object.entries(details);
    if (entries.length === 0) return <span className="text-gray-400">-</span>;

    return (
      <div className="space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="text-xs">
            <span className="text-gray-500">{key}:</span>{' '}
            <span className="text-gray-700">{String(value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
            <p className="text-gray-500 mt-1">Track all administrative actions on the platform</p>
          </div>
          <div className="text-sm text-gray-500">
            {totalCount} entries
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Action Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action Type
              </label>
              <select
                value={filter.actionType || ''}
                onChange={(e) =>
                  setFilter({ ...filter, actionType: e.target.value as AuditActionType | '' })
                }
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[200px]"
              >
                {ACTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filter.search || ''}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  placeholder="Search by user, action, or details..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Clear Filters */}
            {(filter.actionType || filter.search) && (
              <div className="flex items-end">
                <button
                  onClick={() => setFilter({})}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Audit Log Table */}
        {isLoading ? (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="animate-pulse">
              <div className="h-12 bg-gray-100" />
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 border-t border-gray-200">
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <svg
              className="w-12 h-12 mx-auto mb-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-500">No audit log entries found</p>
            {(filter.actionType || filter.search) && (
              <button
                onClick={() => setFilter({})}
                className="mt-2 text-primary-500 hover:text-primary-600"
              >
                Clear filters to see all entries
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatRelativeTime(log.timestamp)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTimestamp(log.timestamp)}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(
                          log.action
                        )}`}
                      >
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {log.performerName}
                        </p>
                        <p className="text-xs text-gray-500">{log.performerEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.targetName ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {log.targetName}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">
                            {log.targetType}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">{renderDetails(log.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Load More */}
        {logs.length > 0 && logs.length < totalCount && (
          <div className="mt-6 text-center">
            <button className="px-6 py-2 text-primary-500 hover:text-primary-600 font-medium">
              Load More
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AuditLogPage;
